import type { rest } from "@codesandbox/api";
import semver from "semver";

function log<T>(...args: T[]) {
  // eslint-disable-next-line
  console.log("[convert-cra]", ...args);
}

type PackageJSON = {
  scripts?: Record<string, string>;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
};

/**
 * Converts a CRA project to a Vite project, Vite runs much more efficient and faster than
 * CRA.
 */
export function convertCRAToVite(
  modules: rest.Sandbox["modules"],
  directories: rest.Sandbox["directories"],
):
  | {
      type: "ok";
      modules: rest.Sandbox["modules"];
    }
  | {
      type: "error";
      error: string;
    } {
  const packageJsonFile = modules.find((m) => m.title === "package.json");

  if (!packageJsonFile) {
    return {
      type: "error",
      error: "Could not find package.json",
    };
  }

  let packageJson: PackageJSON;

  try {
    packageJson = JSON.parse(packageJsonFile.code);
  } catch {
    return {
      type: "error",
      error: "Could not find or parse package.json",
    };
  }

  if (
    !hasDependency(packageJson, "react-scripts") &&
    !packageJson.scripts?.start?.includes("react-scripts")
  ) {
    return {
      type: "error",
      error: "Not a CRA project, could not find react-scripts",
    };
  }

  if (hasDependency(packageJson, "vite")) {
    return {
      type: "error",
      error: "Already a Vite project",
    };
  }

  log("Starting conversion...");

  log("Updating package.json, updating scripts and changing dependencies...");

  packageJson.scripts = {};

  // First update the scripts
  packageJson.scripts["dev"] = "vite";
  if (hasDependency(packageJson, "typescript")) {
    packageJson.scripts["build"] = "tsc && vite build";
  } else {
    packageJson.scripts["build"] = "vite build";
  }

  packageJson.scripts["preview"] = "vite preview";

  removeDependency(packageJson, "react-scripts");
  removeDependency(packageJson, "loader-utils");

  packageJson.devDependencies = packageJson.devDependencies || {};
  packageJson.devDependencies["vite"] = "^5.0.11";
  packageJson.devDependencies["@vitejs/plugin-react"] = "^4.2.1";

  let isClassicReact = false;

  try {
    isClassicReact = packageJson.dependencies?.react
      ? semver.lte(packageJson.dependencies.react, "16.0.0")
      : false;
  } catch {
    // Do not care, hope for the best
  }

  packageJsonFile.code = JSON.stringify(packageJson, null, 2);

  log("Renaming .js files to .jsx...");
  // Now rename all `.js` files containing React code to `.jsx`
  renameJsxFiles(modules);

  const srcDirectoryShortid = directories.find((d) => d.title === "src")
    ?.shortid;

  if (!srcDirectoryShortid) {
    return {
      type: "error",
      error: "Could not find src directory",
    };
  }

  // Find the entrypoint of the app, do it after renaming files to ensure we have the right filename
  const ENTRIES = [
    "index.js",
    "index.ts",
    "index.jsx",
    "index.tsx",
    "main.js",
    "main.ts",
    "main.jsx",
    "main.tsx",
  ];
  const foundEntry = ENTRIES.map((entry) =>
    modules.find(
      (m) => m.title === entry && m.directoryShortid === srcDirectoryShortid,
    ),
  ).filter(Boolean)[0];

  if (!foundEntry) {
    return {
      type: "error",
      error: "Could not find entrypoint",
    };
  }

  const publicDirectoryShortid = directories.find((d) => d.title === "public")
    ?.shortid;

  if (!publicDirectoryShortid) {
    return {
      type: "error",
      error: "Could not find public directory",
    };
  }

  const indexFile = modules.find(
    (m) =>
      m.title === "index.html" && m.directoryShortid === publicDirectoryShortid,
  );

  if (!indexFile) {
    return {
      type: "error",
      error: "Could not find index.html",
    };
  }

  log("Writing new index.html and vite.config.ts...");
  let newHtml;
  try {
    newHtml = rewriteHtml(indexFile.code, foundEntry.title);
  } catch (e) {
    newHtml = `<!doctype html>
  <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Vite + React</title>
    </head>
    <body>
      <div id="root"></div>
      <script type="module" src="/src/${foundEntry}"></script>
    </body>
  </html>
  `;
  }

  // Write the new HTML file
  modules.push({
    code: newHtml,
    title: "index.html",
    directoryShortid: null,
    corrections: [],
    errors: [],
    id: "",
    insertedAt: "",
    isBinary: false,
    isNotSynced: false,
    path: "",
    savedCode: null,
    shortid: "",
    sourceId: "",
    type: "file",
    updatedAt: "",
  });

  modules.push({
    directoryShortid: null,
    title: "vite.config.ts",
    code: `import { defineConfig } from "vite";
  import react from "@vitejs/plugin-react";
  
  // https://vitejs.dev/config/
  export default defineConfig({
      plugins: [react(${isClassicReact ? '{ jsxRuntime: "classic" }' : ""})],
  });
  `,
    corrections: [],
    errors: [],
    id: "",
    insertedAt: "",
    isBinary: false,
    isNotSynced: false,
    path: "",
    savedCode: null,
    shortid: "",
    sourceId: "",
    type: "file",
    updatedAt: "",
  });

  modules.push({
    directoryShortid: srcDirectoryShortid,
    title: "vite-env.d.ts",
    code: '/// <reference types="vite/client" />\n',
    corrections: [],
    errors: [],
    id: "",
    insertedAt: "",
    isBinary: false,
    isNotSynced: false,
    path: "",
    savedCode: null,
    shortid: "",
    sourceId: "",
    type: "file",
    updatedAt: "",
  });

  log("Removing CRA files...");

  modules = modules.filter((m) => {
    if (
      m.title === "index.html" &&
      m.directoryShortid === publicDirectoryShortid
    ) {
      return false;
    }

    return true;
  });

  return { type: "ok", modules };
}

function removeDependency(pkgJson: PackageJSON, dependency: string) {
  if (pkgJson.dependencies?.[dependency]) {
    delete pkgJson.dependencies[dependency];
  }
  if (pkgJson.devDependencies?.[dependency]) {
    delete pkgJson.devDependencies[dependency];
  }
}

function hasDependency(pkgJson: PackageJSON, dependency: string) {
  return Boolean(
    pkgJson.dependencies?.[dependency] || pkgJson.devDependencies?.[dependency],
  );
}

const JSXSyntax = /\n(.*?)<[A-z](.|\n)*?\/?>/;

function getSyntaxInfo(code: string, path: string) {
  // Use getters so we can evaluate lazily
  const syntax = {
    get jsx() {
      const jsxMatch = code.match(JSXSyntax);

      return jsxMatch;
    },
    get dynamicImports() {
      return code.includes("import(");
    },
  };

  if (path.endsWith(".min.js")) {
    // This needs no transpiling and often fools our JSX check with <a etc...
    return {
      jsx: false,
      dynamicImports: false,
    };
  }

  return syntax;
}

async function renameJsxFiles(modules: rest.Sandbox["modules"]) {
  modules.forEach(async (module) => {
    if (module.title.endsWith(".js")) {
      const syntaxInfo = getSyntaxInfo(module.code, module.title);
      if (syntaxInfo.jsx) {
        module.title = module.title.replace(/\.js$/, ".jsx");
      }
    }
  });
}

/**
 * Rewrites HTML to be compatible with Vite.
 */
function rewriteHtml(html: string, entryPoint: string) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Remove all tags that contain %
  const templateTags = doc.querySelectorAll('link[href*="%"]');

  templateTags.forEach((tag) => {
    tag.parentNode?.removeChild(tag);
  });

  const script = document.createElement("script");

  script.type = "module";
  script.src = `/src/${entryPoint}`;

  doc.querySelector("body")?.appendChild(script);

  return doc.documentElement.innerHTML;
}
